import { useEffect } from "react";
import { Router } from "@/app/router";
import { bindQueryRecoveryTriggers } from "@/shared/api";
import { registerKakaoPostcodeAllowlist } from "@/shared/lib/allowlistBridge";
import { registerBridgeErrorHandler } from "@/shared/lib/bridgeError";
import { setBaseExitConfirmationPaths, resetBackContract } from "@/shared/lib/backBridge";
import { bootstrapEasyPayReturn } from "@/features/exam/lib/easyPayRecovery";
import {
  bootstrapSplashReveal,
  bootstrapSplashHidden,
} from "@/shared/lib/splashReveal";
import { ROUTES } from "@/shared/config/routes";

// 뒤로가기 종료 확인 경로 (back 5단계 계약 ③) — 상위 back 목적지가 없는 앱 루트/진입점.
// AOS 하드웨어 백은 이 경로들에서 "앱을 종료하시겠습니까?" 다이얼로그를 띄우고 [종료] 시 finish()
// 로 실제 종료한다(문구·다이얼로그는 native 소유). 그 외 경로는 back = 상위 이동.
// 미인증(/login)도 포함하므로 post-auth 진입점(TabShell)이 아니라 앱 루트에서 등록한다.
// (native 최대 32개 cap — 현재 7개.)
// ⚠️ iOS 는 등록하지 않는다 — 종료 공개 API 부재·종료 다이얼로그가 rootVC dismiss no-op 이라 깨져 보임(#271, ADR 260807-02).
const EXIT_CONFIRMATION_PATHS = [
  ROUTES.MAIN,
  ROUTES.FEES,
  ROUTES.COVERAGE_ANALYSIS,
  ROUTES.MORE,
  ROUTES.LOGIN,
  ROUTES.SIGNUP_TERMS,
  ROUTES.SIGNUP_ONBOARDING,
];

function App() {
  useEffect(() => {
    registerKakaoPostcodeAllowlist();
  }, []);

  // native bridge 오류 통지 수신 채널 앱 전역 등록 (silent consume 제거 + SNS 실패 안내 팝업).
  useEffect(() => registerBridgeErrorHandler(), []);

  // 뒤로가기 종료 확인 경로 baseline 등록 (AOS 전용 — 위 주석의 iOS 제외 사유 참조).
  // 레지스트리가 이 baseline 과 화면별 동적 경로(useExitOnBack)를 union 해 AOS 로만 push 한다
  // (AOS 게이트는 레지스트리 repushExitPaths 내부). WebView reload 시 재실행으로 재등록.
  useEffect(() => {
    setBaseExitConfirmationPaths(EXIT_CONFIRMATION_PATHS);
  }, []);

  // SPA 언로드(카드결제 window.location.assign 등) 직전 네이티브 back 계약 리셋 (best-effort 보조).
  // 전체페이지 네비게이션은 React cleanup 을 건너뛰어 overlay/stepNav 플래그가 stuck 되므로,
  // 언로드 직전에 false 를 push 해 stuck 발생을 줄인다. pagehide 는 WKWebView 에서 beforeunload 보다 신뢰.
  // 단 언로드 직전 push 의 native 도달은 보장되지 않는다 — 확정 복구는 main.tsx 부팅 리셋이 담당한다.
  useEffect(() => {
    const onPageHide = () => resetBackContract();
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, []);

  // 간편결제 복귀(onEasyPayReturn) 전역 수신 + 콜드 in-flight 복구를 /payment/return 로 유도 (#282).
  // window.onEasyPayReturn 은 결제 시작 전에 정의돼 있어야 하므로 앱 루트에서 1회 등록한다.
  useEffect(() => bootstrapEasyPayReturn(), []);

  // 스플래시 노출 개시 콜백(onSplashReveal) 전역 등록 — native 가 스플래시 페이드 시작 시 호출(진입 애니 시작 신호).
  // native 호출은 부팅 뒤 ~2s 라 App 초기 커밋 등록으로 충분(web 은 splashReveal 모듈초기에 이미 revealed).
  useEffect(() => bootstrapSplashReveal(), []);

  // 스플래시 완전 제거 콜백(onSplashHidden) 전역 등록 — native 가 스플래시 페이드 완료 시 호출(프로모션 진입 grow 신호, #325).
  // onSplashReveal(fade-start)과 발화 시점 정반대. web 은 splashReveal 모듈초기에 이미 hidden.
  useEffect(() => bootstrapSplashHidden(), []);

  // 네트워크 오류로 멈춘 조회의 자동 복구 — 재연결(online)·앱 복귀(visibilitychange→visible) 시 재조회 (#419).
  useEffect(() => bindQueryRecoveryTriggers(), []);

  return <Router />;
}

export default App;
